<script setup>
// O SINO (07/10/2026) — a mesma linguagem da central de notificações.
//
// Antes: 15 itens corridos, prazo fatal com o mesmo peso de "nova mensagem",
// e o que importava podia nem estar entre os 15 (61% das notificações em
// produção são "novo andamento"). Agora:
//   - "Precisa de você" no topo: as URGENTES não lidas, buscadas à parte;
//   - o resto agrupado por dia, como a Agenda;
//   - o item é o mesmo da central (ItemDeNotificacao), com o "marcar como
//     lida" à vista;
//   - o clique leva ao mesmo destino da central (um arquivo só).
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useSidebarLayout } from 'dashboard/components-next/sidebar/provider';
import NotificationsAPI from 'dashboard/api/notifications';
import ItemDeNotificacao from 'dashboard/routes/dashboard/notifications/components/ItemDeNotificacao.vue';
import {
  agruparPorDia,
  rotuloDoDia,
} from 'dashboard/routes/dashboard/notifications/agruparPorDia';
import { abrirNotificacao } from 'dashboard/routes/dashboard/notifications/destinoDaNotificacao';
import { DropdownContainer, DropdownBody } from 'next/dropdown-menu/base';

const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();

const notifications = useMapGetter('notifications/getNotifications');
const meta = useMapGetter('notifications/getMeta');
const uiFlags = useMapGetter('notifications/getUIFlags');
const accountId = useMapGetter('getCurrentAccountId');
const { isHorizontal } = useSidebarLayout();

const unreadCount = computed(() => {
  const count = meta.value.unreadCount;
  if (!count) return '';
  return count < 100 ? `${count}` : '99+';
});

const urgentes = ref([]);
const idsUrgentes = computed(() => new Set(urgentes.value.map(n => n.id)));
const recentes = computed(() =>
  notifications.value.slice(0, 15).filter(n => !idsUrgentes.value.has(n.id))
);
const grupos = computed(() => agruparPorDia(recentes.value));
const isUpdating = computed(() => uiFlags.value.isUpdating);

const rotulo = chave =>
  rotuloDoDia(
    chave,
    {
      hoje: t('NOTIFICATIONS_PAGE.CENTRAL.HOJE'),
      ontem: t('NOTIFICATIONS_PAGE.CENTRAL.ONTEM'),
    },
    locale.value
  );

const buscarUrgentes = async () => {
  try {
    const { data } = await NotificationsAPI.listar({
      familia: 'urgente',
      soNaoLidas: true,
    });
    urgentes.value = data.data.payload.slice(0, 5);
  } catch {
    urgentes.value = [];
  }
};

const marcarLida = async n => {
  try {
    await store.dispatch('notifications/read', {
      id: n.id,
      primaryActorId: n.primary_actor_id,
      primaryActorType: n.primary_actor_type,
      unreadCount: meta.value.unreadCount,
    });
  } catch {
    // o sino não para por isso
  }
  urgentes.value = urgentes.value.filter(u => u.id !== n.id);
};

// Ao navegar, o painel fecha — guardado quando abre (o gatilho é quem tem
// o `toggle`; o corpo do dropdown não entrega um "fechar").
let fecharPainel = () => {};

const abrir = n => {
  if (!n.read_at) marcarLida(n);
  fecharPainel();
  abrirNotificacao(router, accountId.value, n);
};

async function markAllRead() {
  try {
    await store.dispatch('notifications/readAll');
    urgentes.value = [];
  } catch {
    // ignore
  }
}

function viewAll() {
  fecharPainel();
  router.push({
    name: 'notifications_index',
    params: { accountId: accountId.value },
  });
}

// A LISTA SÓ QUANDO O PAINEL ABRE (06/10/2026).
//
// Antes a lista inteira era pedida ao montar a barra, em toda página aberta,
// mesmo sem ninguém abrir o sininho. Medido na A&T: 1,3 a 3,5 s de servidor
// por chamada (cada item monta a conversa inteira), 10 chamadas em 40 min. O
// número do sininho não depende dela: vem de `notifications/unReadCount`, que
// a barra já pede ao montar (Sidebar.vue).
const abrirOuFechar = (toggle, isOpen) => {
  if (!isOpen) {
    store.dispatch('notifications/get');
    buscarUrgentes();
    fecharPainel = () => toggle();
  }
  toggle();
};
</script>

<template>
  <DropdownContainer class="relative flex-shrink-0">
    <template #trigger="{ toggle, isOpen }">
      <button
        class="size-8 rounded-lg flex-shrink-0 grid place-content-center relative transition-colors"
        :class="isOpen ? 'bg-n-alpha-2' : 'hover:bg-n-alpha-1'"
        :title="$t('NOTIFICATIONS_PAGE.HEADER')"
        @click="abrirOuFechar(toggle, isOpen)"
      >
        <span class="i-lucide-bell size-4 text-n-slate-11" />
        <span
          v-if="unreadCount"
          class="min-h-2 min-w-2 p-0.5 px-1 bg-n-ruby-9 rounded-lg absolute -top-1 -right-1.5 grid place-items-center text-[9px] leading-none text-n-ruby-3 font-medium"
        >
          {{ unreadCount }}
        </span>
      </button>
    </template>

    <!-- `advup-tema-pagina`: este painel nasce dentro do `<header>` que
         carrega `.sidebar-dark`, entao herdava as cores da barra escura e
         desenhava texto claro sobre o proprio fundo claro — 1,05:1 de
         contraste no tema claro, medido em 07/09. A classe devolve os tokens
         do tema da pagina. -->
    <DropdownBody
      class="advup-tema-pagina sino z-50 p-0 overflow-hidden"
      :class="
        isHorizontal
          ? '!fixed top-[var(--advup-altura-topo)] right-4 left-4 sm:left-auto sm:w-[26rem] w-auto'
          : 'bottom-12 mb-2 ltr:left-0 rtl:right-0 w-[26rem]'
      "
    >
      <div class="sino__topo">
        <span class="sino__tit">
          {{ $t('NOTIFICATIONS_PAGE.HEADER') }}
          <span v-if="meta.unreadCount" class="advup-conta">
            {{ meta.unreadCount.toLocaleString('pt-BR') }}
          </span>
        </span>
        <button
          v-if="meta.unreadCount"
          v-tooltip.bottom="$t('NOTIFICATIONS_PAGE.MARK_ALL_DONE')"
          type="button"
          class="advup-btn advup-btn--vazado advup-btn--compacto sino__todas"
          :aria-label="$t('NOTIFICATIONS_PAGE.MARK_ALL_DONE')"
          :disabled="isUpdating"
          @click.stop="markAllRead"
        >
          <i class="i-ph-checks" />
          <span class="sino__todas-txt">
            {{ $t('NOTIFICATIONS_PAGE.MARK_ALL_DONE') }}
          </span>
        </button>
      </div>

      <div class="sino__rolagem">
        <!-- O que não pode esperar, sempre em cima — mesmo que seja de ontem
             e tenha chegado atrás de vinte andamentos. -->
        <section v-if="urgentes.length" class="sino__urgente">
          <h3 class="sino__secao sino__secao--urgente">
            <i class="i-lucide-siren" aria-hidden="true" />
            {{ t('NOTIFICATIONS_PAGE.CENTRAL.PRECISA_DE_VOCE') }}
          </h3>
          <ul class="sino__lista">
            <ItemDeNotificacao
              v-for="n in urgentes"
              :key="`u-${n.id}`"
              :notificacao="n"
              compacto
              @abrir="abrir"
              @lida="marcarLida"
            />
          </ul>
        </section>

        <section v-for="g in grupos" :key="g.chave">
          <h3 class="sino__secao">{{ rotulo(g.chave) }}</h3>
          <ul class="sino__lista">
            <ItemDeNotificacao
              v-for="n in g.itens"
              :key="n.id"
              :notificacao="n"
              compacto
              @abrir="abrir"
              @lida="marcarLida"
            />
          </ul>
        </section>

        <div
          v-if="!recentes.length && !urgentes.length"
          class="advup-vazio sino__vazio"
        >
          <span class="advup-vazio__icone" aria-hidden="true">
            <i class="i-lucide-check-check" />
          </span>
          <p class="advup-vazio__txt">
            {{ t('NOTIFICATIONS_PAGE.CENTRAL.VAZIO_EM_DIA') }}
          </p>
        </div>
      </div>

      <div class="sino__rodape">
        <button type="button" class="sino__ver" @click="viewAll">
          {{ $t('NOTIFICATIONS_PAGE.UNREAD_NOTIFICATION.ALL_NOTIFICATIONS') }}
          <i class="i-ph-arrow-right" aria-hidden="true" />
        </button>
      </div>
    </DropdownBody>
  </DropdownContainer>
</template>

<style scoped>
.sino__topo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-3, 12px);
  padding: var(--esp-3, 12px) var(--esp-4, 16px);
  border-bottom: 1px solid rgb(var(--slate-4));
}
.sino__tit {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2, 8px);
  font-size: var(--txt-dado, 14px);
  font-weight: 600;
  color: rgb(var(--slate-12));
}
.sino__todas {
  flex-shrink: 0;
  white-space: nowrap;
}
/* No celular o rótulo quebrava em duas linhas e apertava o topo: fica o
   ícone, com o nome na dica e no leitor de tela. */
@media (max-width: 480px) {
  .sino__todas-txt {
    display: none;
  }
}
.sino__rolagem {
  max-height: min(520px, 70vh);
  overflow-y: auto;
  padding: var(--esp-2, 8px) var(--esp-2, 8px) var(--esp-3, 12px);
}
.sino__secao {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: var(--esp-2, 8px) 0 2px;
  padding-inline: var(--esp-3, 12px);
  font-size: var(--txt-selo, 11.5px);
  font-weight: 600;
  color: rgb(var(--slate-11));
}
.sino__secao--urgente {
  color: rgb(var(--ruby-11));
}
.sino__secao--urgente > i {
  width: 13px;
  height: 13px;
}
/* O bloco urgente ganha o chão do alerta — é a única exceção do painel. */
.sino__urgente {
  margin-bottom: var(--esp-2, 8px);
  padding-bottom: var(--esp-1, 4px);
  border-radius: var(--r-4, 12px);
  background: rgb(var(--ruby-2));
}
.sino__lista {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.sino__vazio {
  padding-block: var(--esp-5, 24px);
}
.sino__rodape {
  border-top: 1px solid rgb(var(--slate-4));
}
.sino__ver {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: var(--esp-3, 12px);
  font-size: var(--txt-nota, 12.5px);
  font-weight: 600;
  color: rgb(var(--blue-11));
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: background var(--mov-1, 180ms) var(--mov-curva, ease);
}
.sino__ver:hover {
  background: rgb(var(--blue-2));
}
.sino__ver > i {
  width: 14px;
  height: 14px;
}
</style>
